import axios from 'axios';

// Decode HTML entities in string values from V8 API responses.
// Walks JSON:API-shaped payloads ({ data: [...] } or { data: {...} }) and
// decodes each string attribute in place.
const htmlDecoder = typeof document !== 'undefined' ? document.createElement('textarea') : null;
function decodeEntity(s) {
  if (typeof s !== 'string' || s.indexOf('&') === -1) return s;
  if (!htmlDecoder) return s;
  htmlDecoder.innerHTML = s;
  return htmlDecoder.value;
}
function decodeAttrs(payload) {
  if (!payload || typeof payload !== 'object') return payload;
  const decodeOne = (rec) => {
    if (rec && rec.attributes && typeof rec.attributes === 'object') {
      for (const k of Object.keys(rec.attributes)) {
        rec.attributes[k] = decodeEntity(rec.attributes[k]);
      }
    }
  };
  if (Array.isArray(payload.data)) payload.data.forEach(decodeOne);
  else if (payload.data) decodeOne(payload.data);
  return payload;
}

// API Configuration
const API_BASE_URL = '/Api';
const API_VERSION = 'V8';
const CHAT_API_BASE = '/api/chat';

// OAuth2 Client Configuration
// These credentials are hardcoded for demo purposes
// In production, these should be stored securely and possibly fetched from backend
const OAUTH_CLIENT_ID = 'f60ab793-ef2f-11f0-8e95-4ece5853b2ce';
const OAUTH_CLIENT_SECRET = 'api-client-secret-demo';
const OAUTH_USERNAME = 'apiuser';
const OAUTH_PASSWORD = 'Test!@#$123';

class SuiteCRMAPI {
  constructor() {
    this.accessToken = null;
    this.refreshToken = null;
    this.tokenExpiry = null;

    // Create axios instance with default config
    this.client = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Accept': 'application/vnd.api+json',
        'Content-Type': 'application/json',
      },
    });

    // Add response interceptor to handle token refresh
    this.client.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        // If 401 and we haven't retried yet, try to refresh token
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          try {
            await this.authenticate();
            originalRequest.headers['Authorization'] = `Bearer ${this.accessToken}`;
            return this.client(originalRequest);
          } catch (refreshError) {
            return Promise.reject(refreshError);
          }
        }

        return Promise.reject(error);
      }
    );
  }

  /**
   * Authenticate with OAuth2 password grant
   */
  async authenticate() {
    try {
      // Build form data manually to ensure proper encoding of special characters
      const params = new URLSearchParams();
      params.append('grant_type', 'password');
      params.append('client_id', OAUTH_CLIENT_ID);
      params.append('client_secret', OAUTH_CLIENT_SECRET);
      params.append('username', OAUTH_USERNAME);
      params.append('password', OAUTH_PASSWORD);

      const response = await axios.post(`${API_BASE_URL}/access_token`, params.toString(), {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });

      this.accessToken = response.data.access_token;
      this.refreshToken = response.data.refresh_token;

      // Calculate token expiry (current time + expires_in seconds - 60 second buffer)
      this.tokenExpiry = Date.now() + (response.data.expires_in - 60) * 1000;

      return this.accessToken;
    } catch (error) {
      console.error('Authentication failed:', error);
      throw new Error('Failed to authenticate with SuiteCRM API');
    }
  }

  /**
   * Ensure we have a valid access token
   */
  async ensureAuthenticated() {
    // Check if token exists and is not expired
    if (!this.accessToken || !this.tokenExpiry || Date.now() >= this.tokenExpiry) {
      await this.authenticate();
    }
    return this.accessToken;
  }

  /**
   * Make authenticated API request
   */
  async request(method, endpoint, data = null) {
    await this.ensureAuthenticated();

    const config = {
      method,
      url: `/${API_VERSION}${endpoint}`,
      headers: {
        'Authorization': `Bearer ${this.accessToken}`,
      },
    };

    if (data) {
      config.data = data;
    }

    const response = await this.client(config);
    // V8 API HTML-encodes string fields (> becomes &gt; etc). Decode attributes
    // before handing the payload to components so the UI shows plain text.
    return decodeAttrs(response.data);
  }

  /**
   * GET request
   */
  async get(endpoint) {
    return this.request('GET', endpoint);
  }

  /**
   * POST request
   */
  async post(endpoint, data) {
    return this.request('POST', endpoint, data);
  }

  /**
   * PATCH request
   */
  async patch(endpoint, data) {
    return this.request('PATCH', endpoint, data);
  }

  /**
   * DELETE request
   */
  async delete(endpoint) {
    return this.request('DELETE', endpoint);
  }

  /**
   * Make authenticated request to custom chat API endpoint
   */
  async chatRequest(method, url, data = null) {
    await this.ensureAuthenticated();
    // Append the real user id so the backend can enforce per-user access (axios
    // bypasses the global fetch injector).
    try {
      if (typeof url === 'string' && url.indexOf('/api/chat/') !== -1 && !/[?&]user_id=/.test(url)) {
        const u = JSON.parse(sessionStorage.getItem('user') || 'null');
        if (u && u.id) url += (url.indexOf('?') === -1 ? '?' : '&') + 'user_id=' + encodeURIComponent(u.id);
      }
    } catch (e) { /* ignore */ }
    const config = {
      method,
      url,
      headers: { 'Authorization': `Bearer ${this.accessToken}` },
    };
    if (data) config.data = data;
    const response = await axios(config);
    return response.data;
  }

  /**
   * Get list of modules
   */
  async getModules() {
    return this.get('/meta/modules');
  }

  /**
   * Get records from a module
   */
  async getRecords(module, options = {}) {
    // Use custom endpoint for jcrm_Businesses
    if (module === 'jcrm_Businesses') {
      const params = new URLSearchParams();
      if (options.page) params.append('page', options.page);
      if (options.pageSize) params.append('pageSize', options.pageSize);

      const queryString = params.toString();
      const url = `${CHAT_API_BASE}/businesses.php${queryString ? `?${queryString}` : ''}`;
      return this.chatRequest('GET', url);
    }

    // Use custom endpoint for jcrm_Accounting
    if (module === 'jcrm_Accounting') {
      const params = new URLSearchParams();
      if (options.page) params.append('page', options.page);
      if (options.pageSize) params.append('pageSize', options.pageSize);

      const queryString = params.toString();
      const url = `${CHAT_API_BASE}/accounting.php${queryString ? `?${queryString}` : ''}`;
      return this.chatRequest('GET', url);
    }

    // Use custom endpoint for jcrm_PurchaseOrders
    if (module === 'jcrm_PurchaseOrders') {
      const params = new URLSearchParams();
      if (options.page) params.append('page', options.page);
      if (options.pageSize) params.append('pageSize', options.pageSize);

      const queryString = params.toString();
      const url = `${CHAT_API_BASE}/purchase-orders.php${queryString ? `?${queryString}` : ''}`;
      return this.chatRequest('GET', url);
    }

    // Proposals / Invoices top-level lists go through scoped custom endpoints so
    // a user restricted to assigned projects only sees those projects' records.
    // (Admins / unrestricted users get everything, same as the V8 list.)
    if (module === 'AOS_Quotes' || module === 'AOS_Invoices') {
      const params = new URLSearchParams();
      params.append('page', options.page || 1);
      params.append('pageSize', options.pageSize || 20);
      const file = module === 'AOS_Quotes' ? 'quotes-list.php' : 'invoices-list.php';
      return this.chatRequest('GET', `${CHAT_API_BASE}/${file}?${params.toString()}`);
    }

    // Tasks list goes through project-tasks.php so co-worker/admin visibility
    // applies (admins + co-workers see every task; others see only their own).
    if (module === 'Tasks') {
      const params = new URLSearchParams();
      params.append('scope', 'all');
      params.append('page', options.page || 1);
      params.append('pageSize', options.pageSize || 20);
      if (options.userId) params.append('user_id', options.userId);
      return this.chatRequest('GET', `${CHAT_API_BASE}/project-tasks.php?${params.toString()}`);
    }

    const params = new URLSearchParams();

    // Always include pagination to avoid SuiteCRM LIMIT bug
    params.append('page[number]', options.page || 1);
    params.append('page[size]', options.pageSize || 20);

    if (options.sort) {
      params.append('sort', options.sort);
    }
    if (options.filter) {
      Object.keys(options.filter).forEach(key => {
        params.append(`filter[${key}]`, options.filter[key]);
      });
    }

    const queryString = params.toString();
    const endpoint = `/module/${module}?${queryString}`;

    return this.get(endpoint);
  }

  /**
   * Get a single record by ID
   */
  async getRecord(module, id) {
    // Use custom endpoint for jcrm_Businesses
    if (module === 'jcrm_Businesses') {
      return this.chatRequest('GET', `${CHAT_API_BASE}/businesses.php?id=${id}`);
    }
    // Use custom endpoint for jcrm_Accounting
    if (module === 'jcrm_Accounting') {
      return this.chatRequest('GET', `${CHAT_API_BASE}/accounting.php?id=${id}`);
    }
    // Use custom endpoint for jcrm_PurchaseOrders
    if (module === 'jcrm_PurchaseOrders') {
      return this.chatRequest('GET', `${CHAT_API_BASE}/purchase-orders.php?id=${id}`);
    }
    // Proposals / Invoices single record goes through the scoped endpoint so a
    // user restricted to assigned projects can't open another project's record.
    if (module === 'AOS_Quotes' || module === 'AOS_Invoices') {
      const file = module === 'AOS_Quotes' ? 'quotes-list.php' : 'invoices-list.php';
      return this.chatRequest('GET', `${CHAT_API_BASE}/${file}?id=${id}`);
    }
    return this.get(`/module/${module}/${id}`);
  }

  /**
   * Create a new record
   */
  async createRecord(module, data) {
    // Use custom endpoint for jcrm_Businesses
    if (module === 'jcrm_Businesses') {
      return this.chatRequest('POST', `${CHAT_API_BASE}/businesses.php`, {
        data: { attributes: data }
      });
    }
    // Use custom endpoint for jcrm_Accounting
    if (module === 'jcrm_Accounting') {
      return this.chatRequest('POST', `${CHAT_API_BASE}/accounting.php`, {
        data: { attributes: data }
      });
    }
    // Use custom endpoint for jcrm_PurchaseOrders
    if (module === 'jcrm_PurchaseOrders') {
      return this.chatRequest('POST', `${CHAT_API_BASE}/purchase-orders.php`, {
        data: { attributes: data }
      });
    }
    // Use custom endpoint for AOS_Products (V8 API unreliable for custom _c fields)
    if (module === 'AOS_Products') {
      return this.chatRequest('POST', `${CHAT_API_BASE}/products.php`, data);
    }
    return this.post(`/module`, {
      data: {
        type: module,
        attributes: data,
      },
    });
  }

  /**
   * Update a record
   */
  async updateRecord(module, id, data) {
    // Use custom endpoint for jcrm_Businesses
    if (module === 'jcrm_Businesses') {
      return this.chatRequest('PATCH', `${CHAT_API_BASE}/businesses.php`, {
        data: { id: id, attributes: data }
      });
    }
    // Use custom endpoint for jcrm_Accounting
    if (module === 'jcrm_Accounting') {
      return this.chatRequest('PATCH', `${CHAT_API_BASE}/accounting.php`, {
        data: { id: id, attributes: data }
      });
    }
    // Use custom endpoint for jcrm_PurchaseOrders
    if (module === 'jcrm_PurchaseOrders') {
      return this.chatRequest('PATCH', `${CHAT_API_BASE}/purchase-orders.php`, {
        data: { id: id, attributes: data }
      });
    }
    // Use custom endpoint for AOS_Products (V8 API unreliable for custom _c fields)
    if (module === 'AOS_Products') {
      return this.chatRequest('PATCH', `${CHAT_API_BASE}/products.php`, {
        data: { id: id, attributes: data }
      });
    }
    return this.patch(`/module`, {
      data: {
        type: module,
        id: id,
        attributes: data,
      },
    });
  }

  /**
   * Delete a record
   */
  async deleteRecord(module, id) {
    // Use custom endpoint for jcrm_Businesses
    if (module === 'jcrm_Businesses') {
      return this.chatRequest('DELETE', `${CHAT_API_BASE}/businesses.php?id=${id}`);
    }
    // Use custom endpoint for jcrm_Accounting
    if (module === 'jcrm_Accounting') {
      return this.chatRequest('DELETE', `${CHAT_API_BASE}/accounting.php?id=${id}`);
    }
    // Use custom endpoint for jcrm_PurchaseOrders
    if (module === 'jcrm_PurchaseOrders') {
      return this.chatRequest('DELETE', `${CHAT_API_BASE}/purchase-orders.php?id=${id}`);
    }
    return this.delete(`/module/${module}/${id}`);
  }
}

// Create singleton instance
const api = new SuiteCRMAPI();

export default api;
